<template lang="pug">
    include ../../icon/arrow

    div(
        class="_tabs",
        :class="_class",
        :style="{ width, height }")
        div(class="_tabc", :class="{ _taboh: overflowHack }", :style="{ height: content ? null : height }")
            slot(name="start")
            div(v-if="$device.hasMouse", v-show="showBack", class="_tabback")
                ib-button(class="link mirror-rtl", @click="prevPage")
                    +icon-arrow-back
            div(ref="tabb", class="_tabb", :class="{ _tabsc: !$device.hasMouse }", v-on="$device.hasMouse ? { scroll } : null")
                div(ref="tabl", class="_tabl", role="tablist")
                    ib-tab-title(
                        v-for="(tab,i) in $slots.default",
                        v-if="tabs[i]",
                        ref="tab",
                        :key="i",
                        :vnode="tab",
                        :appearance="appearance",
                        @click="goto")
                div(v-if="appearance != 'clear'", ref="tabu", class="_tabu", aria-hidden="true")
            div(v-if="$device.hasMouse", v-show="showNext", class="_tabnext")
                ib-button(class="link mirror-rtl", @click="nextPage")
                    +icon-arrow-forward
            slot(name="end")
        div(class="_tabp")
            slot
</template>

<script>
    import $device from '../../prototype/device';
    import $util from '../../prototype/util';
    import resize from '../private/resize';
    import ibTabTitle from './ib-tab-title.js';
    import { scrollProps } from '../private/util';

    const transDuration = 250;
    const arrowWidth = 28;
    const trailingSlashRE = /\/?$/;
    const urlParse = /^(([^:\/?#]+:)?(?:\/\/((?:([^\/?#:]*):([^\/?#:]*)@)?([^\/?#:]*)(?::([^\/?#:]*))?)))?([^?#]*)(\?[^#]*)?(#.*)?$/;
    const queryParse = str => str ? JSON.parse('{"' + str.slice(1).replace(/&/g, '","').replace(/=/g, '":"') + '"}', (key,val) => key === '' ? val : decodeURIComponent(val)) : void 0;

    function queryIncludes(current = {}, target = {}) {
        return !Object.keys(target).some(key => current[key] != target[key]);
    }

    function routeEqual(current, target, exact) {
        if (current.path && target.path) {
            const cp = current.path.replace(trailingSlashRE, '/');
            const tp = target.path.replace(trailingSlashRE, '/');
            return exact
                ? cp === tp &&
                  current.hash === target.hash &&
                  queryIncludes(current.query, target.query)
                : cp.startsWith(tp) &&
                  (!target.hash || current.hash === target.hash) &&
                  queryIncludes(current.query, target.query);
        }
        if (current.name && target.name) {
            return exact
                ? current.name === target.name &&
                  current.hash === target.hash &&
                  queryIncludes(current.query, target.query)
                : current.name === target.name &&
                  (!target.hash || current.hash === target.hash) &&
                  queryIncludes(current.query, target.query);
        }
        return false;
    }

    function hrefEqual(current, target, exact) {
        current = current.match(urlParse) || [];
        target = target.match(urlParse) || [];
        return exact
            ? current[8] === target[8] &&
              current[10] === target[10] &&
              queryIncludes(queryParse(current[9]), queryParse(target[9]))
            : current[8] &&
              current[8].startsWith(target[8]) &&
              (!target[10] || current[10] === target[10]) &&
              queryIncludes(queryParse(current[9]), queryParse(target[9]));
    }


    /**
     * This is a user interface element that allows different content blocks to share the same
     * screen space in a mutually exclusive manner. Tabs make it easy to explore and switch
     * among different views or functional aspects of an app. Tabs serve as headings for their
     * respective content; the *active* tab is always visually distinguished from the others.
     * @see Each tab is represented as an instance of [ib-tab](#ibtab).
     *
     * Slots:
     * - `start`: Element to display before start (left) of tab list.
     * - `end`: Element to display after end (right) of tab list.
     */
    export default {
        name: 'ib-tabs',
        mixins: [resize],

        components: {
            ibTabTitle
        },

        props: {
            /**
             * The name of the tab to display. If not specified, the first tab will be shown.
             */
            value: [String,Number],
            /**
             * Appearance modifier. `clear` is supported.
             */
            appearance: String,
            /**
             * Horizontal alignment: `start`, `center`, `end`, or `grow`.
             */
            align: {
                type: String,
                default: 'start'
            },
            /**
             * Tab density. Supported are `compact` and `comfortable`.
             */
            spacing: String,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height of bar and pane in any CSS units.
             */
            height: String
        },

        data() {
            return {
                tabs: [],
                active: -1,
                overflow: 0,
                ready: false,
                content: false,
                trans: null,
                showBack: false,
                showNext: false,
                rseW: $device.hasMouse,
                rseH: false,
                // Older browsers lose track of baseline with overflow:[not visible]
                overflowHack: /Trident\/|Firefox\/[1-4]\d\./.test(navigator.userAgent)
            };
        },

        computed: {
            _class() {
                return {
                    _ol: this.overflow <= 0,
                    _npane: !this.content,
                    ['_e'+this.appearance]: this.appearance,
                    ['_a'+this.align]: this.align,
                    ['_s'+this.spacing]: this.spacing
                };
            },

            sp() {
                const props = scrollProps();
                const rtl = this.$rtl;
                return {
                    base: rtl ? props.rtlBase : 0,
                    step: rtl ? props.rtlStep : 1,
                    barHeight: props.barHeight
                };
            }
        },

        watch: {
            value: 'selectvalue',
            $route: 'selectroute'
        },

        created() {
            this.parseNodes();
        },

        mounted() {
            this.ready = true;
            this.selectauto();
        },

        beforeDestroy() {
            if (this.popstate) {
                removeEventListener('popstate', this.selecthref);
            }
        },

        methods: {
            sync() {
                if (this.ready) {
                    this.parseNodes();
                }
            },

            parseNodes() {
                const slots = this.$slots.default || [];
                const tabs = slots.map(vnode => vnode.componentOptions && vnode.componentOptions.Ctor.options.name === 'ib-tab');
                if (!this.popstate && slots.some((tab,i) => tabs[i] && tab.componentOptions.propsData.href)) {
                    this.popstate = true;
                    addEventListener('popstate', this.selecthref);
                }
                this.tabs = tabs;
                this.content = slots.some((tab,i) => tabs[i] && (tab.componentOptions.children||[]).some(child => {
                    const slot = child.data?.slot;
                    return slot == null || slot == 'default';
                }));
                this.$nextTick(this.selectauto);
            },

            /**
             * Navigate to the tab before the active one, if any.
             */
            prev() {
                if (this.active > 0) {
                    this.goto(this.active - 1);
                }
            },

            /**
             * Navigate to the tab after the active one, if any.
             */
            next() {
                if (this.active < this.$refs.tab.length - 1) {
                    this.goto(this.active + 1);
                }
            },

            /**
             * Set the currently active `ib-tab` element by reference or index and emit an `input` event.
             */
            goto(tab) {
                if (typeof tab !== 'number') {
                    tab = this.$refs.tab.indexOf(tab);
                }
                const value = this._set(tab);
                if (value != null) {
                    this.$emit('input', value);
                }
            },

            /**
             * Set the currently active `ib-tab` element by value, route, or href as appropriate.
             */
            selectauto() {
                if (this.value != null || (!this.selectroute(this.$route) && !this.selecthref())) {
                    this.selectvalue(this.value);
                }
            },

            /**
             * Set the currently active `ib-tab` element by name and emit an `input` event.
             */
            selectvalue(value) {
                const tabs = this.$refs.tab;
                const index = tabs ? tabs.findIndex(value == null
                    ? tab => tab.href == null
                    : tab => tab.data.name == value) : -1;
                if (index >= 0 && index < this.$refs.tab.length) {
                    this._set(index);
                    return true;
                } else {
                    return false;
                }
            },

            /**
             * Set the currently active `ib-tab` element by route and emit an `input` event.
             */
            selectroute(route) {
                const tabs = this.$refs.tab;
                const tab = tabs && tabs.find(tab => tab.data.route != null && routeEqual(route, this.$router.resolve(tab.data.route, route).location, tab.data.exact));
                if (tab) {
                    this.goto(tab);
                    return true;
                } else {
                    const last = tabs && tabs[this.active];
                    if (last && last.data.route) {
                        this._set(-1);
                    }
                    return false;
                }
            },

            /**
             * Set the currently active `ib-tab` element by URL and emit an `input` event.
             */
            selecthref() {
                const tabs = this.$refs.tab;
                const tab = tabs && tabs.find(tab => tab.data.href != null && hrefEqual(location.href, tab.data.href, tab.data.exact));
                if (tab) {
                    this.goto(tab);
                    return true;
                } else {
                    const last = tabs && tabs[this.active];
                    if (last && last.data.href) {
                        this._set(-1);
                    }
                    return false;
                }
            },

            /** @private */
            resize() {
                if (!this.showActive()) {
                    this.scroll();
                }
            },

            /** @private */
            scroll() {
                const tabb = this.$refs.tabb;
                const overflow = tabb.scrollWidth - tabb.clientWidth;
                const left = tabb.scrollLeft;
                const step = this.sp.step;
                this.showBack = overflow > 0 && step * left > step * overflow * this.sp.base + 1;
                this.showNext = overflow > 0 && step * left < step * overflow * (this.sp.base + step) - 1;
                this.overflow = overflow;
            },

            /** @private */
            showActive(behavior) {
                const refs = this.$refs;
                if (refs.tab && refs.tabb) {
                    const tab = refs.tab[this.active];
                    if (tab) {
                        const showBack = this.showBack;
                        const showNext = this.showNext;
                        const hasMouse = $device.hasMouse;
                        if ($util.scrollIntoView(tab.$el, refs.tabb, void 0, {
                            left: hasMouse ? 40 : 8,
                            right: hasMouse ? 40 : 8
                        }, behavior)) {
                            setTimeout(() => {
                                // buttons appeared or disappeared; recenter
                                if (showBack != this.showBack || showNext != this.showNext) {
                                    this.showActive(behavior);
                                }
                            }, 30);
                            return true;
                        } else if (hasMouse) {
                            this.scroll();
                        }
                    }
                }
            },

            /** @private */
            prevPage() {
                const tabb = this.$refs.tabb;
                tabb.scrollBy({ left: -this.sp.step * tabb.clientWidth, behavior: 'smooth' });
            },

            /** @private */
            nextPage() {
                const tabb = this.$refs.tabb;
                tabb.scrollBy({ left: this.sp.step * tabb.clientWidth, behavior: 'smooth' });
            },

            /** @private */
            _set(index) {
                const refs = this.$refs;
                if (!refs.tab) {
                    return;
                }
                while (refs.tab[index] && refs.tab[index].disabled) {
                    index++;
                }
                if (this.active != index) {
                    let last = refs.tab[this.active];
                    let next = refs.tab[index];
                    if (last) {
                        last.active = false;
                        const li = last.vnode.componentInstance;
                        if (li) {
                            li.hide();
                        }
                        if (last.data.tone && refs.tabu) {
                            refs.tabu.classList.remove(last.data.tone);
                        }
                        if (next) {
                            refs.tabb.classList.add('_tr');
                            if (refs.tabu) {
                                refs.tabu.style.left = last.$el.offsetLeft + 'px';
                                refs.tabu.style.width = last.$el.clientWidth + 'px';
                                Object.assign(refs.tabu.style, $util.transform({
                                    tx: next.$el.offsetLeft - last.$el.offsetLeft,
                                    sx: next.$el.clientWidth / last.$el.clientWidth
                                }));
                            }
                            if (this.trans) {
                                clearTimeout(this.trans);
                            }
                            this.trans = setTimeout(() => {
                                const refs = this.$refs;
                                this.trans = null;
                                if (refs.tabb) {
                                    refs.tabb.classList.remove('_tr');
                                }
                                if (refs.tabu) {
                                    refs.tabu.style.left =
                                    refs.tabu.style.width = '';
                                    Object.assign(refs.tabu.style, $util.transform({
                                        tx: 0,
                                        sx: 1
                                    }));
                                }
                            }, transDuration);
                        }
                    }
                    this.active = index;
                    if (next) {
                        next.active = true;
                        const ni = next.vnode.componentInstance;
                        if (ni) {
                            ni.show();
                        }
                        if (next.data.tone && refs.tabu) {
                            refs.tabu.classList.add(next.data.tone);
                        }
                        this.$nextTick(() => { this.showActive('smooth') });
                        return next.data.name;
                    } else {
                        return null;
                    }
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tabs {
        display: flex;
        flex-direction: column;

        /* For resize mixin on legacy browsers */
        position: relative;

        /* The following can be overridden by font and color classes */
        font-weight: 500;
        color: @fg-heading;
        border-color: @fg-accent;

        /* No pane */
        &._npane {
            > ._tabp {
                display: none;
            }
        }
    }
    ._tabc {
        display: flex;
        flex: 0 0 auto;
        align-items: baseline;
        height: 3em;
        border-color: inherit;
        overflow-y: hidden;
    }
    ._taboh {
        align-items: center;
    }
    ._tabb {
        display: flex;
        flex: 1 1 auto;
        position: relative;
        line-height: 1;
        height: 100%;
        overflow: hidden;
        border-color: inherit;
    }
    ._tabsc {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        /* Hide scrollbar on mobile */
        height: calc(~'100% + 20px');
        margin-bottom: -20px;
        padding-bottom: 20px;
        ._tabu {
            bottom: 20px;
        }
    }
    ._tabl {
        display: flex;
        flex: 1 0 auto;
        align-items: stretch;
        border-color: inherit;
    }
    ._tabt {
        position: relative;
        -webkit-tap-highlight-color: transparent;
        border-bottom: @tab-border solid transparent;
        margin: 0 @tab-margin;
        padding: 0 @tab-padding;
        white-space: nowrap;
        text-align: center;
        text-decoration: none !important;
        color: inherit !important;
        cursor: pointer;
        opacity: 1;
        outline: none;
        &[aria-selected=true] {
            border-bottom-color: inherit;
        }
        &[disabled] {
            opacity: .15;
            cursor: default;
        }
        > ._bdgw {
            top: calc(~'50% - 2em') !important;
        }
    }
    ._tabs._scompact ._tabt {
        & when (isunit(@tab-margin-compact, px)) {
            margin: 0 @tab-margin-compact;
        }
        & when (isunit(@tab-padding-compact, px)) {
            padding: 0 @tab-padding-compact;
        }
    }
    ._tabs._scomfortable ._tabt {
        & when (isunit(@tab-margin-comfort, px)) {
            margin: 0 @tab-margin-comfort;
        }
        & when (isunit(@tab-padding-comfort, px)) {
            padding: 0 @tab-padding-comfort;
        }
    }
    ._tabt,
    ._tabtc {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    ._tabtc {
        opacity: 1;
        transition: opacity .2s;
    }
    ._tabtn {
        opacity: .42;
        &:hover when (@have-hover) {
            opacity: .7;
            transition: none;
        }
    }
    ._tabp {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        border-top: 1px solid @border-default;
        overflow-x: hidden;
        overflow-y: auto;
    }
    ._tabps {
        flex: 1;
    }
    ._tabback,
    ._tabnext {
        display: flex;
        width: 0;
        z-index: 1;
        align-self: stretch;
        > button {
            flex: 0 0 auto;
            width: 32px;
            border-radius: 0;
            padding-bottom: @tab-border !important;
            background-color: @bg !important;
        }
    }
    ._tabback {
        justify-content: flex-start;
    }
    ._tabnext {
        justify-content: flex-end;
    }

    ._tabu {
        position: absolute;
        bottom: 0;
        border-bottom: @tab-border solid;
        border-bottom-color: inherit;
        opacity: 0;
        will-change: transform;
        transform-origin: left;
    }
    ._tabb {
        &._tr {
            ._tabt {
                border-bottom-color: transparent !important;
            }
            ._tabu {
                opacity: 1;
                transition:
                    transform .2s cubic-bezier(.2,.6,.4,1),
                    border-bottom-color .2s linear;
            }
        }
    }

    /** Alignment */
    ._tabs {
        &._astart._ol {
            ._tabl {
                justify-content: flex-start;
            }
            ._tabt._aend {
                -webkit-margin-start: auto;
                margin-inline-start: auto;
            }
        }
        &._acenter._ol {
            ._tabl {
                justify-content: center;
            }
        }
        &._aend._ol {
            ._tabl {
                justify-content: flex-end;
            }
            ._tabt._astart {
                -webkit-margin-end: auto;
                margin-inline-end: auto;
            }
        }
        &._agrow._ol {
            ._tabl {
                align-content: space-between;
            }
            ._tabt {
                flex: 1 1 auto;
            }
        }
    }

    /** Clear tabs style */
    ._tabs._eclear {
        ._tabt {
            margin: 0 6px;
            border: none;
            &[aria-selected=true] {
                font-weight: 600;
            }
        }
        &._astart ._tabt {
            -webkit-margin-start: 0;
            -webkit-margin-end: 12px;
            margin-inline-start: 0;
            margin-inline-end: 12px;
        }
        &._aend ._tabt {
            -webkit-margin-start: 12px;
            -webkit-margin-end: 0;
            margin-inline-start: 12px;
            margin-inline-end: 0;
        }
    }
    ._tabci {
        visibility: hidden;
        height: 0;
        font-weight: 600;
    }
</style>

<docs>
    Basic tabs:
    ```jsx
    <ib-context>
        <ib-tabs height="160px">
            <ib-tab title="Starks">
                <ib-col>
                    <ul>
                        <li>Eddard</li>
                        <li>Catelyn</li>
                        <li>Robb</li>
                        <li>Sansa</li>
                        <li>Brandon</li>
                        <li>Arya</li>
                        <li>Rickon</li>
                    </ul>
                </ib-col>
            </ib-tab>
            <ib-tab title="Lannisters">
                <ib-col>
                    <ul>
                        <li>Tywin</li>
                        <li>Cersei</li>
                        <li>Jamie</li>
                        <li>Tyrion</li>
                    </ul>
                </ib-col>
            </ib-tab>
            <ib-tab title="Targaryens">
                <ib-col>
                    <ul>
                        <li>Viserys</li>
                        <li>Daenerys</li>
                    </ul>
                </ib-col>
            </ib-tab>
        </ib-tabs>
    </ib-context>
    ```

    Width-growing tabs with icons (with one disabled):
    ```jsx
    <ib-context>
        <ib-tabs align="grow">
            <ib-tab title="Bitcoin">
                <div slot="title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M4 2a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm5 3h1.4v1.5h2.2V5H14v1.81c.3.1.61.18.81.38.3.2.58.5.78.9.2.4.32.82.32 1.32 0 .2 0 .48-.1.68l-.31.6c-.1.2-.3.4-.5.5-.2.2-.39.3-.69.4.3.1.58.21.78.41.2.2.4.3.5.6.1.2.21.41.32.71.1.2.09.48.09.78.1.9-.2 1.72-.9 2.22-.3.2-.7.4-1.1.5v1.69h-1.4V17h-2.2v1.5H9V17h-.81V6.5H9zm1.4 3.31V11H12c.6-.1.99-.2 1.19-.4.3-.2.4-.5.4-1s-.1-.8-.4-1c-.3-.2-.68-.29-1.28-.29zm-.09 4.19v2.9h1.88c.3 0 .52.01.72-.09.2-.1.4-.2.5-.31.1-.1.18-.3.28-.4.1-.2.12-.4.12-.6 0-.2-.02-.4-.12-.6 0-.2 0-.4-.19-.5-.1-.1-.3-.2-.5-.3-.2-.1-.39-.1-.69-.1z"/></svg>
                </div>
                <p class="inset-lg">This is some bitcoin related content.</p>
            </ib-tab>
            <ib-tab title="Swiss Franc" disabled="disabled">
                <div slot="title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20 2H4a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-4.2 6.2h-4.7V11h4.2v1.8h-4.2v1.4H13v1.3h-1.9v1.6H8.9v-1.6h-1v-1.3h1V6.5h6.9z"/></svg>
                </div>
                <p class="inset-lg">This is some Swiss Franc related content.</p>
            </ib-tab>
            <ib-tab title="Euro">
                <div slot="title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20 2H4a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-5.8 8.2v1.1h-2.5v.9h2.5v1.1h-2.5c0 .4.1.8.2 1 .1.3.3.5.5.7.2.2.4.3.7.4.3.1.6.1.9.1.2 0 .5 0 .7-.1.2 0 .5-.1.7-.2l.3 1.8c-.3.1-.6.1-.9.2-.3 0-.6.1-1 .1-.6 0-1.2-.1-1.7-.2-.5-.2-1-.4-1.3-.7-.4-.3-.7-.7-.9-1.2-.2-.5-.3-1.1-.4-1.7H8.3v-1.1h1.2v-.9H8.3v-1.1h1.3c0-.8.2-1.4.4-1.9s.5-.9.9-1.2c.4-.3.8-.6 1.3-.7.5-.2 1.1-.2 1.7-.2.3 0 .6 0 .9.1.3 0 .6.1.9.2l-.3 1.8c-.2-.1-.4-.1-.7-.2-.3-.1-.5-.1-.7-.1-.3 0-.6 0-.9.1-.3.1-.5.2-.7.4-.1.1-.2.3-.4.6s-.2.6-.2 1h2.4z"/></svg>
                </div>
                <p class="inset-lg">This is some Euro related content.</p>
            </ib-tab>
            <ib-tab title="Ruble">
                <div slot="title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M4 2a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm4 2h6a4 4 0 1 1 0 8h-4v2h4v2h-4v4H8v-4H6v-2h2v-2H6v-2h2zm2 2v4h4a2 2 0 1 0 0-4z"/></svg>
                </div>
                <p class="inset-lg">This is some ruble related content.</p>
            </ib-tab>
            <ib-tab title="Yen">
                <div slot="title">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20 2H4a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-4.6 9.7V13h-2.3v.8h2.3v1.3h-2.3v2h-2.2v-2H8.4v-1.3h2.5V13H8.4v-1.3h1.9L7.6 6.5H10l2 4.4 2-4.4h2.4l-2.7 5.2z"/></svg>
                </div>
                <p class="inset-lg">This is some Japanese yen related content.</p>
            </ib-tab>
        </ib-tabs>
    </ib-context>
    ```

    Clear Tabs:
    ```jsx
    <ib-context>
        <ib-tabs appearance="clear">
            <ib-tab title="1D">
                <p class="inset-lg">This is some content for one day.</p>
            </ib-tab>
            <ib-tab title="7D">
                <p class="inset-lg">This is some content for seven days.</p>
            </ib-tab>
            <ib-tab title="MTD">
                <p class="inset-lg">This is some content for month-to-date.</p>
            </ib-tab>
            <ib-tab title="1M">
                <p class="inset-lg">This is some content for one month.</p>
            </ib-tab>
            <ib-tab title="3M">
                <p class="inset-lg">This is some content for three months.</p>
            </ib-tab>
        </ib-tabs>
    </ib-context>
    ```
</docs>
